import { useId, useMemo, type JSX } from 'react';
import { useIntl } from '@doxyme/locale';
import {
  ModalContent,
  ModalBody,
  ModalHeader,
  Modal,
  ModalVideoPreview,
} from '@doxyme/ui/components/foundations';
import { VirtualBackgroundsList } from './virtualBackgroundsList/virtualBackgroundsList';
import {
  VirtualBackgroundGroups,
  type GroupVirtualBackground,
  type VirtualBackground,
} from './constants';
import { ModalId } from '../../../features/modals/modals.type';
import { useAppSelector } from '../../../hooks/useReduxToolkit';
import { getIsModalOpen } from '../../../features/modals/modals.selector';

export type VirtualBackgroundsProps = {
  hasVideo: boolean;
  hasVideoAccess: boolean;
  backgrounds: VirtualBackground[];
  videoElement: HTMLVideoElement | HTMLElement | undefined;
  onSelectVirtualBackground: (background: VirtualBackground) => Promise<void>;
  storedBackground: VirtualBackground;
  onClose?: () => void;
  isApplyingVirtualBackground: boolean;
};

export const VirtualBackgrounds = ({
  hasVideo,
  hasVideoAccess,
  backgrounds,
  videoElement,
  storedBackground,
  onClose = (): void => undefined,
  onSelectVirtualBackground,
  isApplyingVirtualBackground,
}: VirtualBackgroundsProps): JSX.Element => {
  const { formatMessage } = useIntl();

  const groupedBackgrounds: GroupVirtualBackground[] = useMemo(() => {
    const groups = [];
    for (const group in VirtualBackgroundGroups) {
      const filteredBackgrounds = backgrounds
        .filter((background) => background.group === group)
        .sort(
          (background1, background2) => background1.index - background2.index,
        );

      groups.push({
        key: group as VirtualBackgroundGroups,
        backgrounds: filteredBackgrounds,
      });
    }

    return groups.filter((group) => group.backgrounds.length);
  }, [backgrounds]);

  const isVirtualBackgroundOpen = useAppSelector((state) =>
    getIsModalOpen(state, ModalId.VirtualBackground),
  );

  const titleId = useId();

  return (
    <Modal isOpen={isVirtualBackgroundOpen} aria-labelledby={titleId}>
      <ModalContent>
        <ModalHeader
          onClose={onClose}
          title={formatMessage({ id: 'virtualBackground' })}
          titleId={titleId}
        />
        <ModalBody
          scrollType='sectioned'
          fixedSection={
            <ModalVideoPreview
              type='virtualBackground'
              hasVideo={hasVideo}
              hasVideoAccess={hasVideoAccess}
              showProgressBar={isApplyingVirtualBackground}
              videoElement={videoElement}
            />
          }
          scrollableSection={
            <VirtualBackgroundsList
              backgroundList={groupedBackgrounds}
              selectedBackgroundIndex={storedBackground.index}
              onSelect={onSelectVirtualBackground}
            />
          }
        />
      </ModalContent>
    </Modal>
  );
};
